import { createPortal } from 'react-dom'
import { useEffect, useRef, useState, type KeyboardEvent } from 'react'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
  icon?: string | null | undefined
}

function OptionIcon({ src, alt }: { src?: string | null | undefined; alt: string }) {
  const [failed, setFailed] = useState(false)
  if (!src || failed) return null
  return <img src={src} alt={alt} className="h-4 w-4 shrink-0 rounded-sm object-contain opacity-90" onError={() => setFailed(true)} />
}

interface SelectProps {
  value: string
  options: SelectOption[]
  onChange: (value: string) => void
  placeholder: string
  disabled?: boolean
  ariaLabel: string
}

interface MultiSelectProps {
  values: string[]
  options: SelectOption[]
  onChange: (values: string[]) => void
  placeholder: string
  disabled?: boolean
  ariaLabel: string
}

export function Select({ value, options, onChange, placeholder, disabled = false, ariaLabel }: SelectProps) {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, above: false })
  const selected = options.find((option) => option.value === value)

  function updatePosition() {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    const estimatedHeight = Math.min(280, Math.max(48, options.length * 42))
    const above = rect.bottom + 8 + estimatedHeight > window.innerHeight && rect.top - 8 - estimatedHeight > 0
    setPosition({
      top: above ? rect.top - 8 : rect.bottom + 8,
      left: rect.left,
      width: rect.width,
      above,
    })
  }

  useEffect(() => {
    if (!open) return
    updatePosition()

    function handleOutside(event: PointerEvent) {
      const target = event.target as Node
      if (!triggerRef.current?.contains(target) && !popupRef.current?.contains(target)) setOpen(false)
    }

    function handleViewportChange() {
      updatePosition()
    }

    document.addEventListener('pointerdown', handleOutside)
    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('scroll', handleViewportChange, true)
    return () => {
      document.removeEventListener('pointerdown', handleOutside)
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('scroll', handleViewportChange, true)
    }
  }, [open, options.length])

  function choose(option: SelectOption) {
    if (option.disabled) return
    onChange(option.value)
    setOpen(false)
    requestAnimationFrame(() => triggerRef.current?.focus())
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const enabledOptions = options.filter((option) => !option.disabled)
    const currentIndex = Math.max(0, enabledOptions.findIndex((option) => option.value === value))
    let nextIndex = currentIndex

    if (event.key === 'ArrowDown') nextIndex = Math.min(enabledOptions.length - 1, currentIndex + 1)
    else if (event.key === 'ArrowUp') nextIndex = Math.max(0, currentIndex - 1)
    else if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = enabledOptions.length - 1
    else if (event.key === 'Escape') {
      setOpen(false)
      return
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setOpen((current) => !current)
      return
    } else {
      return
    }

    event.preventDefault()
    const next = enabledOptions[nextIndex]
    if (next) choose(next)
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((current) => !current)
        }}
        onKeyDown={handleKeyDown}
        className="input flex items-center justify-between gap-3 text-left transition data-[open=true]:border-accent-400 disabled:cursor-not-allowed disabled:opacity-50"
        data-open={open}
      >
        <span className={`flex min-w-0 items-center gap-2 ${selected ? 'text-white' : 'text-surface-500'}`}>
          <OptionIcon src={selected?.icon} alt="" />
          <span className="truncate">{selected?.label ?? placeholder}</span>
        </span>
        <svg viewBox="0 0 16 16" className={`h-4 w-4 shrink-0 text-surface-500 transition ${open ? 'rotate-180 text-accent-200' : ''}`} fill="none" aria-hidden="true">
          <path d="m4 6 4 4 4-4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
        </svg>
      </button>

      {open ? createPortal(
        <div
          ref={popupRef}
          role="listbox"
          aria-label={ariaLabel}
          className="fixed z-[100] max-h-[280px] overflow-y-auto rounded-xl border border-white/[0.12] bg-[#111719] p-1.5 shadow-[0_18px_50px_rgba(0,0,0,0.45)]"
          style={{
            left: position.left,
            width: position.width,
            ...(position.above ? { bottom: window.innerHeight - position.top } : { top: position.top }),
          }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              disabled={option.disabled}
              onClick={() => choose(option)}
              className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition ${option.value === value ? 'bg-accent-300/10 text-accent-100' : 'text-surface-300 hover:bg-white/[0.07] hover:text-white'} disabled:cursor-not-allowed disabled:opacity-40`}
            >
              <span className="flex min-w-0 items-center gap-2">
                <OptionIcon src={option.icon} alt="" />
                <span className="truncate">{option.label}</span>
              </span>
              {option.value === value ? <span className="ml-3 text-accent-200" aria-hidden="true">✓</span> : null}
            </button>
          ))}
        </div>,
        document.body,
      ) : null}
    </>
  )
}

export function MultiSelect({ values, options, onChange, placeholder, disabled = false, ariaLabel }: MultiSelectProps) {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, above: false })
  const selectedOptions = options.filter((option) => values.includes(option.value))

  function updatePosition() {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    const estimatedHeight = Math.min(280, Math.max(48, options.length * 42))
    const above = rect.bottom + 8 + estimatedHeight > window.innerHeight && rect.top - 8 - estimatedHeight > 0
    setPosition({
      top: above ? rect.top - 8 : rect.bottom + 8,
      left: rect.left,
      width: rect.width,
      above,
    })
  }

  useEffect(() => {
    if (!open) return
    updatePosition()

    function handleOutside(event: PointerEvent) {
      const target = event.target as Node
      if (!triggerRef.current?.contains(target) && !popupRef.current?.contains(target)) setOpen(false)
    }

    function handleViewportChange() {
      updatePosition()
    }

    document.addEventListener('pointerdown', handleOutside)
    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('scroll', handleViewportChange, true)
    return () => {
      document.removeEventListener('pointerdown', handleOutside)
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('scroll', handleViewportChange, true)
    }
  }, [open, options.length])

  function toggle(option: SelectOption) {
    if (option.disabled) return
    onChange(values.includes(option.value) ? values.filter((value) => value !== option.value) : [...values, option.value])
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'Escape') {
      setOpen(false)
      return
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setOpen((current) => !current)
    }
  }

  const summary = selectedOptions.length === 0
    ? placeholder
    : selectedOptions.length === 1
      ? selectedOptions[0]?.label ?? placeholder
      : `${selectedOptions.length} ${ariaLabel.toLowerCase()}`

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((current) => !current)
        }}
        onKeyDown={handleKeyDown}
        className="input flex items-center justify-between gap-3 text-left transition data-[open=true]:border-accent-400 disabled:cursor-not-allowed disabled:opacity-50"
        data-open={open}
      >
        <span className={selectedOptions.length ? 'truncate text-white' : 'truncate text-surface-500'}>{summary}</span>
        <svg viewBox="0 0 16 16" className={`h-4 w-4 shrink-0 text-surface-500 transition ${open ? 'rotate-180 text-accent-200' : ''}`} fill="none" aria-hidden="true">
          <path d="m4 6 4 4 4-4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
        </svg>
      </button>

      {open ? createPortal(
        <div
          ref={popupRef}
          role="listbox"
          aria-label={ariaLabel}
          aria-multiselectable="true"
          className="fixed z-[100] max-h-[280px] overflow-y-auto rounded-xl border border-white/[0.12] bg-[#111719] p-1.5 shadow-[0_18px_50px_rgba(0,0,0,0.45)]"
          style={{
            left: position.left,
            width: position.width,
            ...(position.above ? { bottom: window.innerHeight - position.top } : { top: position.top }),
          }}
        >
          {options.map((option) => {
            const isSelected = values.includes(option.value)
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                disabled={option.disabled}
                onClick={() => toggle(option)}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition ${isSelected ? 'bg-accent-300/10 text-accent-100' : 'text-surface-300 hover:bg-white/[0.07] hover:text-white'} disabled:cursor-not-allowed disabled:opacity-40`}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <OptionIcon src={option.icon} alt="" />
                  <span className="truncate">{option.label}</span>
                </span>
                <span className={`ml-3 flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[11px] ${isSelected ? 'border-accent-300 bg-accent-300 text-surface-950' : 'border-white/25'}`} aria-hidden="true">{isSelected ? '✓' : ''}</span>
              </button>
            )
          })}
        </div>,
        document.body,
      ) : null}
    </>
  )
}
